import { useMemo, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchCostDetail, type CostDetailRow } from '../../api/admin.js';
import { nanoToUsd, nanoToUsdCompact, formatNumber } from '../../lib/format.js';
import type { CsvColumn } from '../../lib/csv.js';
import { ReportFilters, useReportFilters } from './components/ReportFilters.js';
import { ExportCsvButton } from './components/ExportCsvButton.js';
import { MetricChart, type MetricSeries } from './components/MetricChart.js';

const CSV_COLUMNS: CsvColumn<CostDetailRow>[] = [
  { header: 'operation', value: (r) => r.operation },
  { header: 'kind', value: (r) => r.kind },
  { header: 'model', value: (r) => r.model },
  { header: 'provider', value: (r) => r.provider },
  { header: 'calls', value: (r) => r.callCount },
  { header: 'cost_usd', value: (r) => nanoToUsd(r.costNano) },
  { header: 'avg_cost_usd', value: (r) => nanoToUsd(r.avgCostNano) },
];

export function CostDetailPage(): ReactElement {
  const filters = useReportFilters();
  const [searchParams] = useSearchParams();

  // Drill-down a las transacciones de una operación, preservando los filtros
  // actuales (van como query params compartidos: from/to/user/module/…).
  const txHref = (operation: string | null): string => {
    const p = new URLSearchParams(searchParams);
    if (operation) p.set('operation', operation);
    else p.delete('operation');
    return `/reports/transactions?${p.toString()}`;
  };

  const query = useQuery({
    queryKey: ['admin', 'reports', 'cost-detail', filters.key],
    queryFn: () => fetchCostDetail(filters.toParams()),
  });

  const rows = query.data ?? [];

  const chart = useMemo(() => {
    const top = rows.slice(0, 16);
    const categories = top.map((r) => r.operation ?? '—');
    const series: MetricSeries[] = [
      { key: 'costo', values: top.map((r) => Number(BigInt(r.costNano))) },
    ];
    return { categories, series };
  }, [rows]);

  const grandTotalNano = useMemo(
    () => rows.reduce((a, r) => a + BigInt(r.costNano), 0n).toString(),
    [rows],
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Detalle de gasto</h1>
          <p className="page-sub">
            En qué se fue la plata, por operación ·{' '}
            <span className="mono">
              {filters.from} → {filters.to}
            </span>{' '}
            · <span className="mono">{nanoToUsdCompact(grandTotalNano)}</span> total
            <br />
            <span className="tt-sec">
              Filtrá por <span className="mono">model</span> arriba para aislar un modelo. El modelo
              mostrado es el que realmente corrió (span); para LLM puede diferir del label de billing.
            </span>
          </p>
        </div>
        <ExportCsvButton filename="cost-detail" rows={rows} columns={CSV_COLUMNS} />
      </div>

      <ReportFilters state={filters} />

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <div className="pad-7">
          <section className="card" style={{ marginBottom: 24 }}>
            <header className="card-head">
              <h3 className="card-title">Top operaciones por costo</h3>
            </header>
            <MetricChart
              categories={chart.categories}
              series={chart.series}
              formatValue={(n) => nanoToUsdCompact(BigInt(Math.round(n)).toString())}
            />
          </section>

          <section className="card">
            <header className="card-head">
              <h3 className="card-title">Items</h3>
              <span className="mono tt-sec">{formatNumber(rows.length)} operaciones</span>
            </header>
            <table className="table" data-testid="cost-detail-table">
              <thead>
                <tr>
                  <th>Operación</th>
                  <th>Kind</th>
                  <th>Modelo</th>
                  <th className="r">Llamadas</th>
                  <th className="r">Costo</th>
                  <th className="r">Costo/llamada</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center' }}>
                      Sin datos en el rango / filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={`${r.operation ?? '—'}-${r.model ?? '—'}-${r.kind}-${i}`}>
                      <td className="mono tt-sec">
                        <Link to={txHref(r.operation)} title="Ver transacciones de esta operación">
                          {r.operation ?? '—'}
                        </Link>
                      </td>
                      <td className="mono tt-sec">
                        <span className="badge">{r.kind}</span>
                      </td>
                      <td className="mono tt-sec">
                        {r.model ?? <span className="muted">—</span>}
                        {r.provider ? <span className="muted"> · {r.provider}</span> : null}
                      </td>
                      <td className="r mono">{formatNumber(r.callCount)}</td>
                      <td className="r mono">{nanoToUsdCompact(r.costNano)}</td>
                      <td className="r mono">{nanoToUsdCompact(r.avgCostNano)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </>
  );
}
